import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box, Grid, Typography, Button,Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import OrderSummary from "./../SalesList/OrderSummary";
import DeliveryDetails from "./../SalesList/DeliveryDetails";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { API_BASE_URL } from "../../config/constants";
import SalesTable from "./../../components/SalesRecordPage/SalesTable";

// Extend dayjs with the localizedFormat plugin
dayjs.extend(localizedFormat);

export default function SalesRecordArchive() {
  const [sales, setSales] = useState([]);
  const [items, setItems] = useState([]);
  const [storedCart, setStoredCart] = useState({});
  const [comboCart, setComboCart] = useState({});
  const [combos, setCombos] = useState([]);
  // NEW – controls the confirmation dialog visibility
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const [form, setForm] = useState({
    items: {},
    name: "",
    salesDate: dayjs(),
    price: "",
    buyerDetails: "",
    phoneNumber: "",
    pincode: "",
    state: "KL",
    coupon: "",
    salesStatus: "SC",
    salesType: "NORMAL",
    createdSource: "MOBILE",
    createdFrom: "ADMIN",
    systemDate: dayjs(),
    giveAway: false,
    extraDiscount: "",
    additionalDiscount: [],
    shipping: "",
    email: "",
    extraDiscountDescription: "",
  });
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchSales();
        const itemsResponse = await axios.get(`${API_BASE_URL}/items`);
        setItems(itemsResponse.data || []);

        const response = await axios.get(`${API_BASE_URL}/combo`);
        setCombos(response.data || []);
      } catch (error) {
        console.error("Failed to fetch data", error);
      }
    };

    fetchData();
  }, []);

  const fetchSales = async () => {
    try {
      const salesResponse = await axios.get(
        `${API_BASE_URL}/salesbystatusfromarchive`,
        {
          params: { sales_status: "SP" },
        }
      );
      setSales(salesResponse.data || []);
    } catch (error) {
      console.error("❌ Failed to fetch sales data", error);
    }
  };

  const getSaleItemsWithImages = (saleItems, allItems) => {
    const tempStoredCart = {};
    const tempCombo = {};

    Object.keys(saleItems).forEach((itemId) => {
      if (saleItems[itemId][2] === 1) {
        const matchedItem = combos.find((item) => item.id == itemId);
        if (matchedItem) {
          tempCombo[itemId] = {
            comboId: matchedItem.id,
            comboname: matchedItem.comboname,
            price: matchedItem.price,
            items: addComboObjectsEdit(matchedItem.items),
          };
        }
      } else {
        const matchedItem = allItems.find(
          (item) => item.inventoryid === itemId
        );
        if (matchedItem) {
          tempStoredCart[itemId] = {
            image: matchedItem.image,
            label: matchedItem.label,
            price: saleItems[itemId][1],
            quantity: saleItems[itemId][0],
          };
        }
      }
    });

    setStoredCart(tempStoredCart);
    setComboCart(tempCombo);
  };

  const addComboObjectsEdit = (inventories) => {
    const tempComboCartArray = inventories
      .map((inventoryid) => {
        const item = items.find((item) => item.inventoryid === inventoryid);
        if (item) {
          return {
            inventoryid: item.inventoryid,
            image: item.image,
            label: item.label,
            price: item.sellingprice,
            quantity: 1,
            discount: item.discount,
            sellingprice: item.sellingprice,
          };
        }
        return null;
      })
      .filter(Boolean);

    return tempComboCartArray;
  };

  const setItemsInForm = (itemsObject) => {
    setForm((prevForm) => ({
      ...prevForm,
      items: itemsObject,
    }));
  };

  const setStoredCartItems = (itemsObject) => {
    setStoredCart(itemsObject);
  };

  const setCouponInForm = (coupon) => {
    setForm({ ...form, coupon: coupon });
  };

  const setCartPriceInForm = (itemsObject) => {
    setForm((form) => ({
      ...form,
      price: itemsObject,
    }));
  };

  const handleEdit = (id) => {
    const saleToEdit = sales.find((sale) => sale.id === id);
    getSaleItemsWithImages(saleToEdit.items, items);
    setForm({
      ...saleToEdit,
      salesDate: dayjs().format("YYYY-MM-DD"),
      systemDate: dayjs().format("YYYY-MM-DD"),
      items: saleToEdit.items || {},
      price: saleToEdit.price,
      pincode: saleToEdit.pincode,
      state: saleToEdit.state,
      coupon: saleToEdit.coupon,
      extraDiscount: saleToEdit.extraDiscount,
      extraDiscountDescription: saleToEdit.extraDiscountDescription,
      email: saleToEdit.email,
    });

    setIsPreviewVisible(true);
    window.scrollTo(0, 0);
  };
  const handleRestore = async (saleId) => {
    if (!saleId) return alert("Sale ID not found!");

    try {
      const res = await axios.post(`${API_BASE_URL}/sales/restore`, {
        id: saleId,
      });
      alert("Sale successfully restored to active sales.");
      setIsPreviewVisible(false); // optionally hide preview
      fetchSales(); // reload archive list
    } catch (err) {
      console.error("❌ Restore failed:", err);
      alert("Failed to restore sale. Please try again.");
    }
  };

  const handleDeletePermanent = async (saleId) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/sales/delete-archive`, {
        id: saleId,
      });
      alert("Sale permanently deleted.");
      setIsPreviewVisible(false);
      setIsConfirmDeleteOpen(false);
      fetchSales(); // refresh archive list
    } catch (err) {
      console.error("❌ Delete failed:", err.message);
      alert("Failed to delete sale. Please try again.");
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {isPreviewVisible && (
        <Grid container spacing={2} id="PREVIEW">
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Preview Sales</Typography>
            <DeliveryDetails form={form} isEditMode={true} />
          </Grid>
          <Grid item xs={12} md={6}>
            <OrderSummary
              setItems={setItemsInForm}
              setStoredCartItems={setStoredCartItems}
              setCartPrice={setCartPriceInForm}
              cartItems={storedCart}
              action="Checkout"
              coupon={form.coupon}
              comboCart={comboCart}
              deliveryState={form.state}
            />
            <Grid item xs={12} sx={{ textAlign: "center", marginTop: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleRestore(form.id)}
                sx={{ mr: 2 }}
              >
                Back to Sales
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={() => setIsConfirmDeleteOpen(true)}
              >
                Delete Permanently
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3} sx={{ marginTop: "10px" }}>
        <Grid item xs={12}>
          <SalesTable sales={sales} handleEdit={handleEdit} />
        </Grid>
      </Grid>
      <Dialog
        open={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete this sale?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDeleteOpen(false)}>Cancel</Button>
          <Button color="error" onClick={() => handleDeletePermanent(form.id)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
